export interface PendingJoinRoom {
  roomId: string;
  topic: string;
}

export const JOIN_REQUEST_SENT_EVENT = 'join-request-sent';
export const JOIN_REQUEST_DECLINED_UPDATED_EVENT = 'join-request-declined-updated';

function storageKey(userPk: string | number) {
  return `pendingJoinRoomRequests:${userPk}`;
}

export function getPendingJoinRooms(userPk: string | number): PendingJoinRoom[] {
  try {
    const raw = sessionStorage.getItem(storageKey(userPk));
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function savePendingJoinRooms(userPk: string | number, rooms: PendingJoinRoom[]) {
  sessionStorage.setItem(storageKey(userPk), JSON.stringify(rooms));
}

export function addPendingJoinRoom(
  userPk: string | number,
  roomId: string | number,
  topic = 'this room'
) {
  const id = String(roomId);
  const rooms = getPendingJoinRooms(userPk).filter((r) => r.roomId !== id);
  rooms.push({ roomId: id, topic });
  savePendingJoinRooms(userPk, rooms);
}

export function updatePendingJoinRoomTopic(
  userPk: string | number,
  roomId: string | number,
  topic: string
) {
  const id = String(roomId);
  const rooms = getPendingJoinRooms(userPk);
  const index = rooms.findIndex((r) => r.roomId === id);
  if (index === -1) {
    return;
  }
  rooms[index] = { ...rooms[index], topic };
  savePendingJoinRooms(userPk, rooms);
}

export function removePendingJoinRoom(userPk: string | number, roomId: string | number) {
  const id = String(roomId);
  const rooms = getPendingJoinRooms(userPk).filter((r) => r.roomId !== id);
  savePendingJoinRooms(userPk, rooms);
}

export function markJoinRequestPending(
  userPk: string | number,
  roomId: string | number,
  topic = 'this room'
) {
  clearJoinRequestDeclined(userPk, roomId);
  addPendingJoinRoom(userPk, roomId, topic);
  window.dispatchEvent(
    new CustomEvent(JOIN_REQUEST_SENT_EVENT, {
      detail: { roomId: String(roomId), topic },
    })
  );
}

function declinedStorageKey(userPk: string | number) {
  return `declinedJoinRoomRequests:${userPk}`;
}

export function getDeclinedJoinRooms(userPk: string | number): string[] {
  try {
    const raw = sessionStorage.getItem(declinedStorageKey(userPk));
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

export function markJoinRequestDeclined(userPk: string | number, roomId: string | number) {
  const id = String(roomId);
  if (getDeclinedJoinRooms(userPk).includes(id)) {
    return;
  }
  const rooms = getDeclinedJoinRooms(userPk).filter((r) => r !== id);
  rooms.push(id);
  sessionStorage.setItem(declinedStorageKey(userPk), JSON.stringify(rooms));
  window.dispatchEvent(
    new CustomEvent(JOIN_REQUEST_DECLINED_UPDATED_EVENT, {
      detail: { roomId: id },
    })
  );
}

export function clearJoinRequestDeclined(userPk: string | number, roomId: string | number) {
  const id = String(roomId);
  const rooms = getDeclinedJoinRooms(userPk).filter((r) => r !== id);
  sessionStorage.setItem(declinedStorageKey(userPk), JSON.stringify(rooms));
}

export function isJoinRequestDeclined(
  userPk: string | number,
  roomId: string | number | undefined | null
): boolean {
  if (roomId == null) {
    return false;
  }
  return getDeclinedJoinRooms(userPk).includes(String(roomId));
}
